import { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';

const WorkbookContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export const useWorkbook = () => {
  const context = useContext(WorkbookContext);
  if (!context) {
    throw new Error('useWorkbook must be used within a WorkbookProvider');
  }
  return context;
};

const initialFormData = {
  // User info
  userName: '',
  startDate: new Date().toISOString().split('T')[0],
  
  // Section I: Stability
  rule1: {
    weeklyIncome: '',
    incomeFrequency: '',
    totalMonthlyIncome: '',
    incomeNotes: '',
    completed: false,
  },
  rule2: {
    billsAccountAmount: '',
    spendingAccountAmount: '',
    savingsAccountAmount: '',
    bankName: '',
    accountNotes: '',
    completed: false,
  },
  rule3: {
    payDates: [],
    bills10th: [],
    bills25th: [],
    planningNotes: '',
    completed: false,
  },
  rule4: {
    currentExpenses: [],
    cutsToMake: [],
    monthlyTarget: '',
    completed: false,
  },
  
  // Section II: Control
  rule5: {
    emergencyGoal: '',
    currentEmergencyFund: '',
    monthlyContribution: '',
    completed: false,
  },
  rule6: {
    irregularExpenses: [],
    monthlySetAside: '',
    completed: false,
  },
  rule7: {
    lumpSumAmount: '',
    allocationPlan: [],
    completed: false,
  },
  rule8: {
    debts: [],
    snowballOrder: [],
    monthlyDebtPayment: '',
    completed: false,
  },
  
  // Section III: Growth
  rule9: {
    creditCards: [],
    creditScore: '',
    creditGoals: '',
    completed: false,
  },
  rule10: {
    housingStatus: '',
    monthlyHousingCost: '',
    affordabilityCheck: '',
    housingGoals: '',
    completed: false,
  },
  rule11: {
    carLoanAmount: '',
    carPayment: '',
    bnplItems: [],
    completed: false,
  },
  
  // Section IV: Healing & Purpose
  rule12: {
    moneyStory: '',
    healingSteps: [],
    completed: false,
  },
  rule13: {
    topValues: [],
    purposeStatement: '',
    completed: false,
  },
  rule14: {
    businessIdea: '',
    separateAccounts: false,
    taxPlan: '',
    completed: false,
  },
  
  // Section V: Capstone
  capstone: {
    currentSeason: '',
    values: [],
    goals90Days: [],
    goals12Months: [],
    goals3to5Years: '',
    commitmentStatement: '',
    completed: false,
  },
  
  // Appendices trackers
  appendices: {
    weeklyAverages: [],
    checkInSheets: [],
    debtTracker: [],
    creditChecklist: [],
    emergencyTracker: [],
    irregularPlanner: [],
    crisisNotes: '',
    resetNotes: '',
  },
  
  // Progress tracking
  completedPages: [],
  currentPage: 'title',
  bookmarks: [],
  notes: {},
};

export const WorkbookProvider = ({ children }) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState(initialFormData);
  const [loaded, setLoaded] = useState(false);
  const [syncStatus, setSyncStatus] = useState('idle'); // idle | saving | saved | error
  const saveTimer = useRef(null);

  const [currentPage, setCurrentPage] = useState('title');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [darkMode, setDarkMode] = useState(false);

  // Load saved progress from the backend on mount
  useEffect(() => {
    let active = true;
    async function load() {
      if (!user) { setLoaded(true); return; }
      try {
        const res = await fetch(API_URL + '/api/progress', { credentials: 'include' });
        if (res.ok) {
          const json = await res.json();
          if (active && json.data && Object.keys(json.data).length > 0) {
            setFormData(prev => ({ ...initialFormData, ...json.data }));
          }
        }
      } catch (err) {
        console.error('Failed to load progress:', err);
      } finally {
        if (active) setLoaded(true);
      }
    }
    load();
    return () => { active = false; };
  }, [user]);

  // Debounced save to backend whenever formData changes (after initial load)
  useEffect(() => {
    if (!loaded || !user) return;
    setSyncStatus('saving');
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        const res = await fetch(API_URL + '/api/progress', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ data: formData })
        });
        setSyncStatus(res.ok ? 'saved' : 'error');
      } catch (err) {
        console.error('Failed to save progress:', err);
        setSyncStatus('error');
      }
    }, 800);
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
  }, [formData, loaded, user]);
  
  // Handle dark mode
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);
  
  const updateFormData = (section, data) => {
    setFormData(prev => {
      // If data is a simple value (string, number, etc.), set it directly
      if (typeof data !== 'object' || data === null) {
        return { ...prev, [section]: data };
      }
      // If it's a function, call it with the previous value
      if (typeof data === 'function') {
        return { ...prev, [section]: data(prev[section]) };
      }
      // If it's an object, merge it with the previous object
      return { ...prev, [section]: { ...prev[section], ...data } };
    });
  };
  
  const markPageComplete = (pageId) => {
    setFormData(prev => ({
      ...prev,
      completedPages: prev.completedPages.includes(pageId) 
        ? prev.completedPages 
        : [...prev.completedPages, pageId]
    }));
  };
  
  const toggleBookmark = (pageId) => {
    setFormData(prev => ({
      ...prev,
      bookmarks: prev.bookmarks.includes(pageId)
        ? prev.bookmarks.filter(id => id !== pageId)
        : [...prev.bookmarks, pageId]
    }));
  };
  
  const addNote = (pageId, note) => {
    setFormData(prev => ({
      ...prev,
      notes: { ...prev.notes, [pageId]: note }
    }));
  };
  
  const resetWorkbook = () => {
    if (window.confirm('Are you sure you want to reset all your progress? This cannot be undone.')) {
      setFormData(initialFormData);
      setCurrentPage('title');
    }
  };
  
  const exportData = () => {
    const dataStr = JSON.stringify(formData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'rules-of-money-workbook-data.json';
    link.click();
    URL.revokeObjectURL(url);
  };
  
  const calculateProgress = () => {
    const totalPages = 35; // Total content pages
    const completed = formData.completedPages.length;
    return Math.round((completed / totalPages) * 100);
  };
  
  return (
    <WorkbookContext.Provider value={{
      formData,
      setFormData,
      updateFormData,
      currentPage,
      setCurrentPage,
      sidebarOpen,
      setSidebarOpen,
      darkMode,
      setDarkMode,
      markPageComplete,
      toggleBookmark,
      addNote,
      resetWorkbook,
      exportData,
      calculateProgress,
      syncStatus,
      loaded,
    }}>
      {children}
    </WorkbookContext.Provider>
  );
};
